import {inject} from '@angular/core';
import {Auth, signInWithCustomToken} from '@angular/fire/auth';
import {CanMatchFn, Router, UrlSegment} from '@angular/router';
import {SessionService, SentryService, User, PermissionsService} from '@q9elements/ui-kit/common';
import {map} from 'rxjs';

import {ROUTES_PATH} from '../models/routes-path.enum';
import {USER_STATE} from '../models/user-state.enum';
import {LanguageSuggestionService} from '../services/language-suggestion.service';

export const SessionGuard: CanMatchFn = (route, segments) => {
  const sessionService = inject(SessionService);
  const permissionsService = inject(PermissionsService);
  const sentryService = inject(SentryService);
  const [root, index] = segments;
  const [hasValidTeamIndex, teamIndex] = formatTeamIndex(index);

  if (sessionService.user() && hasValidTeamIndex && sessionService.teamIndex() === teamIndex) {
    return true;
  }

  const router = inject(Router);
  const isSpaceIndexRoute = root?.path === 't';

  if (isSpaceIndexRoute) {
    if (hasValidTeamIndex) {
      sessionService.user.set({
        userInTeam: {
          teamIndex: teamIndex
        }
      } as never);
    }
  }

  const auth = inject(Auth);
  const languageSuggestionService = inject(LanguageSuggestionService);

  return sessionService.startSession().pipe(
    map(() => {
      const user = sessionService.user()!;

      if (user.state !== USER_STATE.ACTIVE) {
        return router.createUrlTree(['']);
      }

      languageSuggestionService.restoreUserSavedLocale(user);
      languageSuggestionService.initializeLanguageSuggestion(user);

      signInWithCustomToken(auth, user.firebaseToken!);
      sentryService.setContext(user as User);

      if (!isSpaceIndexRoute) {
        return true;
      }

      return handleSpaceNavigation();
    })
  );

  function handleSpaceNavigation() {
    if (!permissionsService.hasActiveSpaces()) {
      // This is workaround to pass condition at 17 row next time session guard checks the user in team
      sessionService.updateUser({
        userInTeam: {
          teamIndex: 0
        }
      } as never);

      return router.createUrlTree(['t', 0, ROUTES_PATH.SPACES]);
    }

    if (!hasValidTeamIndex) {
      return router.createUrlTree(['t', sessionService.teamIndex() || 0]);
    }

    return true;
  }
};

function formatTeamIndex(index?: UrlSegment): [boolean, number?] {
  if (!index) {
    return [false];
  }

  const teamIndex = Number(index.path);

  return [true, teamIndex];
}
